import Seo from "../../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand, EASE } from "../../components/Shared";
import { motion } from "framer-motion";
import { CERAMIC, photo, fmt } from "../../data/site";
import { Shield, Droplets, Sun, Gauge, ShieldCheck, Check } from "lucide-react";
import { Link } from "react-router-dom";

export default function Ceramic() {
  return (
    <>
      <Seo
        title="System X Ceramic Coating in Sioux Falls, SD | From $1,800 — A+ Detailing"
        description="Automotive System X ceramic coatings in Sioux Falls: Pro from $1,800 (6-year warranty), Max G+ from $2,000 (lifetime) and Phantom from $2,500 (lifetime). Free paint correction & Carfax registration included."
        path="/ceramic-coatings"
      />
      <PageHero
        eyebrow="Ceramic Coatings"
        lines={["DECADES", "OF GLOSS."]}
        lead="System X ceramic coatings bond to your paint for years of hydrophobic gloss, UV defense and chemical resistance — professionally installed in Sioux Falls."

        chips={["System X Pro — up to 6 years", "Max G+ — up to 10 years · lifetime", "Phantom — lifetime", "Free paint correction included", "Registered to Carfax"]}
        testPrefix="ceramic"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal><Eyebrow>Choose your protection</Eyebrow></Reveal>
        <Reveal delay={0.02} className="mt-2">
          <img src="/systemx-logo.svg" alt="System X ceramic coatings logo" className="h-14 w-auto" loading="lazy" data-testid="systemx-logo" />
        </Reveal>
        <Reveal delay={0.05}>
          <div data-testid="ceramic-note" className="mt-6 flex items-start gap-3 rounded-xl border border-glow/30 bg-glow/10 px-5 py-4 max-w-3xl">
            <ShieldCheck size={16} className="text-glow shrink-0 mt-0.5" />
            <p className="text-sm text-white leading-relaxed">
              <strong>Free paint correction included</strong> with every ceramic booking — and your coating is <strong>registered to your vehicle's Carfax</strong>.
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {CERAMIC.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
            >
              <div
                data-testid={`ceramic-package-${i}`}
                className={`h-full rounded-2xl border p-8 ${
                  c.featured ? "border-bright/50 bg-gradient-to-b from-bright/10 to-card blue-glow" : "border-white/10 bg-card"
                }`}
              >
                {c.featured && (
                  <span className="inline-block bg-bright text-white text-[11px] font-mono uppercase tracking-widest rounded-full px-3 py-1 mb-4">
                    Highest level
                  </span>
                )}
                <h2 className="font-display text-2xl font-extrabold tracking-tight">{c.name}</h2>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display text-4xl font-extrabold">{fmt(c.price)}</span>
                  <span className="text-muted text-sm mb-1.5">starting</span>
                </div>
                <p className="mt-4 text-xs font-mono text-bright">{c.warrantyLine}</p>
                <p className="mt-4 text-sm text-steel leading-relaxed">{c.blurb}</p>
                <ul className="mt-5 space-y-2">
                  {c.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-steel">
                      <Check size={14} className="text-bright mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/book"
                  data-testid={`ceramic-book-${i}`}
                  className={`mt-7 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${
                    c.featured ? "bg-bright hover:bg-accent text-white" : "border border-white/20 hover:border-bright hover:text-bright text-white"
                  }`}
                >
                  Book {c.name}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 text-xs text-muted">
            Coating facts per System X's published product data. Pro and Max G+ are applied to automotive paint and gel coat (Max G+ also wraps and PPF) — for boats and aircraft, System X Phantom is the coating rated for marine and aviation:{" "}
            <Link to="/marine-aviation-ceramic" className="text-bright hover:text-white transition-colors">Marine & Aviation Ceramic</Link>.
          </p>
        </Reveal>
      </section>

      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Eyebrow>Why ceramic</Eyebrow></Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Droplets, t: "Hydrophobic finish", d: "Water beads and sheets off, keeping the finish cleaner for longer." },
              { icon: Sun, t: "UV defense", d: "Helps shield paint from sun exposure and fading over the coating's life." },
              { icon: Shield, t: "Chemical resistance", d: "A sacrificial layer standing between your clear coat and the road." },
              { icon: Gauge, t: "Years, not months", d: "Up to 10 years of protection — backed by real System X warranties." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-card p-7" data-testid={`ceramic-benefit-${i}`}>
                  <b.icon size={22} className="text-bright" />
                  <h3 className="mt-4 font-display text-lg font-bold">{b.t}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand testPrefix="ceramic" />
    </>
  );
}
